import { useState, useEffect } from "react";
import { toast } from "sonner";
import { monitorAPI, analysisAPI } from "../../lib/api";
const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const timeAgo = (dateStr) => {
  if (!dateStr) return "N/A";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const sevColor = s => s === "material" ? "#EF4444" : s === "minor" ? "#F59E0B" : "#22C55E";
const Badge = ({ label, color = ACCENT }) => (
  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px" }}>{label}</span>
);
const Tag = ({ label, color = TEXT_DIM }) => (
  <span style={{ fontSize: 11, color, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 8px", fontFamily: "'DM Sans', sans-serif" }}>{label}</span>
);

const COMPANIES_MOCK = [];

const SNAPSHOTS = []; // Previously seeded with demo snapshot history (Stripe/Adyen/Klarna changes). Now empty — the Change Detail Panel reads real snapshot data from the monitoring API.

/* ── Change Detail Panel ── */
const ChangeDetailPanel = ({ company, onClose }) => {
  const [activeSnap, setActiveSnap] = useState(0);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200,
      display: "flex", alignItems: "flex-start", justifyContent: "flex-end",
      backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 680, height: "100vh", background: SURFACE,
          borderLeft: `1px solid ${BORDER}`, overflowY: "auto",
          animation: "slideIn 0.25s ease",
        }}
      >
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>
                {company.name}
              </h2>
              <Badge label={`${company.deltaCount} CHANGES`} color={sevColor(company.severity)} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {[company.industry, company.region, `Last scan: ${company.lastScan}`].map((t, i) => (
                <span key={i} style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{i > 0 && "· "}{t}</span>
              ))}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_MUTED, display: "flex", padding: 4 }}>
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Deltas */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em", marginBottom: 14 }}>
            CHANGES DETECTED · {company.lastScan.toUpperCase()}
          </div>
          {company.deltas.map((d, i) => (
            <div key={i} style={{
              background: SURFACE_2, border: `1px solid ${sevColor(d.severity)}33`,
              borderRadius: 9, padding: "14px 16px", marginBottom: 10,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <Badge label={d.module.toUpperCase()} color={TEXT_MUTED} />
                <Badge label={d.severity.toUpperCase()} color={sevColor(d.severity)} />
                <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginLeft: "auto" }}>{d.field}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 10, alignItems: "center" }}>
                <div style={{ background: "#200A0A", border: "1px solid #7F1D1D", borderRadius: 7, padding: "10px 12px" }}>
                  <div style={{ fontSize: 9, color: "#EF4444", fontFamily: "'DM Mono', monospace", marginBottom: 4, letterSpacing: "0.06em" }}>BEFORE</div>
                  <div style={{ fontSize: 12, color: "#FCA5A5", fontFamily: "'DM Sans', sans-serif" }}>{d.before || "—"}</div>
                </div>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2}><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                <div style={{ background: "#022E0E", border: "1px solid #14532D", borderRadius: 7, padding: "10px 12px" }}>
                  <div style={{ fontSize: 9, color: "#22C55E", fontFamily: "'DM Mono', monospace", marginBottom: 4, letterSpacing: "0.06em" }}>AFTER</div>
                  <div style={{ fontSize: 12, color: "#86EFAC", fontFamily: "'DM Sans', sans-serif" }}>{d.after}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Historical Snapshots */}
        <div style={{ padding: "20px 24px" }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em", marginBottom: 16 }}>
            HISTORICAL SNAPSHOTS
          </div>
          {SNAPSHOTS.map((snap, i) => (
            <div
              key={i}
              onClick={() => setActiveSnap(i)}
              style={{
                background: activeSnap === i ? `${ACCENT}0A` : SURFACE_2,
                border: `1px solid ${activeSnap === i ? ACCENT + "44" : BORDER}`,
                borderRadius: 9, padding: "14px 16px", marginBottom: 10, cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: activeSnap === i ? 14 : 0 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: i === 0 ? ACCENT : TEXT_DIM, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{snap.date}</span>
                <span style={{ fontSize: 11, color: TEXT_DIM, marginLeft: "auto", fontFamily: "'DM Mono', monospace" }}>by {snap.author}</span>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2} style={{ transform: activeSnap === i ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}><path d="M9 18l6-6-6-6" /></svg>
              </div>
              {activeSnap === i && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingLeft: 14 }}>
                  {[
                    { label: "WHAT WE BELIEVED", text: snap.believed, color: "#93C5FD" },
                    { label: "WHAT CHANGED", text: snap.changed, color: "#FCA5A5" },
                    snap.validated && { label: "WHAT WAS VALIDATED", text: snap.validated, color: "#86EFAC" },
                  ].filter(Boolean).map((s, j) => (
                    <div key={j}>
                      <div style={{ fontSize: 9, color: s.color, fontFamily: "'DM Mono', monospace", marginBottom: 4, letterSpacing: "0.08em" }}>{s.label}</div>
                      <p style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>{s.text}</p>
                    </div>
                  ))}
                  {snap.evidence.length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                      {snap.evidence.map(e => {
                        // Strip internal Exa pipeline call names (`exa_*:` /
                        // `call*:`) before rendering — users must never see
                        // internal pipeline references.
                        const clean = String(e).trim().replace(/^(exa_[^:\s]*|call[^:\s]*)\s*:\s*/i, "").trim();
                        const isUrl = /^https?:\/\/\S+$/i.test(clean);
                        const style = {
                          fontSize: 10, color: ACCENT, background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`,
                          borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontFamily: "'DM Mono', monospace",
                          textDecoration: "none", display: "inline-block",
                        };
                        return isUrl ? (
                          <a key={e} href={clean} target="_blank" rel="noopener noreferrer" style={style}>{clean}</a>
                        ) : (
                          <button key={e} style={style}>{clean}</button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ padding: "0 24px 24px", display: "flex", gap: 10 }}>
          <button style={{
            flex: 1, background: SURFACE_2, border: `1px solid ${BORDER}`,
            borderRadius: 9, padding: "11px", fontSize: 13, color: TEXT_MUTED,
            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          }}>Acknowledge Changes</button>
        </div>
      </div>
    </div>
  );
};

/* ── Expanded Company Row Card ── */
const CompanyRow = ({ co, onViewChange, onOpenResults, onTogglePause, onCheckNow, checking }) => {
  const [expanded, setExpanded] = useState(false);
  const [openState, setOpenState] = useState("idle"); // idle | loading | notfound
  const fitCol = s => s >= 80 ? "#22C55E" : s >= 60 ? "#F59E0B" : "#EF4444";

  const handleOpen = async (e) => {
    e.stopPropagation();
    setOpenState("loading");
    try {
      await onOpenResults(co);
    } catch (err) {
      console.error("Open results failed:", err);
      toast.error("Could not load analysis for this company.");
    }
    // If still mounted and openState is loading, it means navigation didn't happen
    setTimeout(() => setOpenState(prev => prev === "loading" ? "notfound" : prev), 3000);
  };

  return (
    <div style={{
      background: SURFACE, border: `1px solid ${co.changed ? sevColor(co.severity) + "44" : BORDER}`,
      borderRadius: 10, overflow: "hidden", marginBottom: 8,
      transition: "border-color 0.2s",
    }}>
      {/* Row */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", cursor: "pointer" }}
        onClick={() => setExpanded(e => !e)}>
        {/* Status dot */}
        <div style={{
          width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
          background: co.status === "Active" ? "#22C55E" : TEXT_DIM,
          boxShadow: co.status === "Active" ? "0 0 6px #22C55E66" : "none",
        }} />

        {/* Name + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{co.name}</span>
            {co.changed && <Badge label={`${co.deltaCount} CHANGES`} color={sevColor(co.severity)} />}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {[co.industry, co.region, co.domain].map((t, i) => (
              <span key={i} style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{i > 0 ? "· " : ""}{t}</span>
            ))}
          </div>
        </div>

        {/* Fit score */}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: fitCol(co.fitScore), fontFamily: "'DM Sans', sans-serif", lineHeight: 1 }}>{co.fitScore}</div>
          <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 2 }}>FIT</div>
        </div>

        {/* Scan times */}
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "'DM Mono', monospace" }}>Last: {co.lastScan}</div>
          <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>Next: {co.nextScan}</div>
        </div>

        {/* Status badge */}
        <Badge label={co.status.toUpperCase()} color={co.status === "Active" ? "#22C55E" : TEXT_DIM} />

        {/* Chevron */}
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2}
          style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
          <path d="M9 18l6-6-6-6" />
        </svg>
      </div>

      {/* Expanded */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${BORDER}`, padding: "14px 18px" }}>
          {co.changed && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 8, letterSpacing: "0.08em" }}>
                CHANGED MODULES
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {co.changedModules.map(m => <Badge key={m} label={m} color={sevColor(co.severity)} />)}
              </div>
              {co.deltas.slice(0, 2).map((d, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                  background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7, marginBottom: 6,
                }}>
                  <Badge label={d.severity.toUpperCase()} color={sevColor(d.severity)} />
                  <span style={{ fontSize: 12, color: TEXT_MUTED }}>{d.module} · {d.field}</span>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2} style={{ flexShrink: 0 }}><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  <span style={{ fontSize: 12, color: TEXT_PRIMARY, marginLeft: "auto" }}>{d.after}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            {co.changed && (
              <button data-testid="view-change-detail-btn" onClick={(e) => { e.stopPropagation(); onViewChange(co); }} style={{
                background: `${sevColor(co.severity)}14`, border: `1px solid ${sevColor(co.severity)}44`,
                borderRadius: 7, padding: "9px 16px", fontSize: 12, fontWeight: 600,
                color: sevColor(co.severity), cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
              }}>View Full Change Detail →</button>
            )}
            <button data-testid="open-results-btn" onClick={handleOpen} style={{
              background: openState === "loading" ? `${ACCENT}18` : openState === "notfound" ? "#EF444418" : SURFACE_2,
              border: `1px solid ${openState === "loading" ? ACCENT : openState === "notfound" ? "#EF444444" : BORDER}`,
              borderRadius: 7, padding: "9px 16px", fontSize: 12,
              color: openState === "loading" ? ACCENT : openState === "notfound" ? "#EF4444" : TEXT_MUTED,
              cursor: openState === "loading" ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif",
            }}>{openState === "loading" ? "Loading..." : openState === "notfound" ? "No analysis found" : "Open Results"}</button>
            <button
              data-testid={`check-now-btn-${co.monitor_id || co.id}`}
              onClick={(e) => { e.stopPropagation(); onCheckNow && onCheckNow(co); }}
              disabled={checking}
              style={{
                background: checking ? `${ACCENT}18` : SURFACE_2,
                border: `1px solid ${checking ? ACCENT : BORDER}`,
                borderRadius: 7, padding: "9px 16px", fontSize: 12,
                color: checking ? ACCENT : TEXT_MUTED,
                cursor: checking ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif",
                display: "flex", alignItems: "center", gap: 7,
              }}
            >
              {checking && <span style={{ width: 11, height: 11, border: `2px solid ${ACCENT}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />}
              {checking ? "Checking…" : "Check Now"}
            </button>
            <button
              data-testid="pause-monitoring-btn"
              onClick={(e) => { e.stopPropagation(); onTogglePause && onTogglePause(co); }}
              style={{
              background: "transparent", border: `1px solid ${BORDER}`,
              borderRadius: 7, padding: "9px 16px", fontSize: 12, color: TEXT_DIM,
              cursor: "pointer", fontFamily: "'DM Sans', sans-serif", marginLeft: "auto",
            }}>{co.status === "Active" ? "Pause Monitoring" : "Resume Monitoring"}</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── ROOT ── */
export default function InferenceMonitored({ onViewAnalysis, workspaceId }) {
  const [filterStatus, setFilterStatus] = useState("All");
  const [changeTarget, setChangeTarget] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [checkingId, setCheckingId] = useState(null);

  const mapMonitors = (data) => (data || []).map((m, idx) => ({
    id: m.monitor_id || (idx + 1),
    monitor_id: m.monitor_id,
    name: m.company_name || "Unknown",
    domain: m.domain || "N/A",
    industry: m.domain ? m.domain.replace(/\.\w+$/, "").replace(/^www\./, "") : "N/A",
    region: "N/A",
    status: m.status === "active" ? "Active" : "Paused",
    lastScan: m.last_scan ? timeAgo(m.last_scan) : "Never",
    nextScan: m.next_scan ? timeAgo(m.next_scan) : "—",
    fitScore: m.fit_score || 0,
    changed: m.changed || false,
    severity: m.severity || null,
    deltaCount: m.delta_count || 0,
    changedModules: m.changed_modules || [],
    deltas: m.deltas || [],
  }));

  useEffect(() => {
    const fetchMonitors = async () => {
      try {
        const res = await monitorAPI.list();
        setCompanies(mapMonitors(res.data));
      } catch (err) {
        console.error("Failed to fetch monitors:", err);
        toast.error("Could not load monitored companies.");
      }
    };
    fetchMonitors();
  }, []);

  // Manual change-detection run for one monitor. The backend runs the check in
  // a background task, so we poll the list once after a short delay to pick up
  // the new scan time / change flags.
  const handleCheckNow = async (co) => {
    if (!co || !co.monitor_id) {
      toast.error("Cannot check this monitor — missing monitor id.");
      return;
    }
    if (checkingId) return;
    setCheckingId(co.monitor_id);
    try {
      await monitorAPI.checkNow(co.monitor_id);
      toast.success(`Checking ${co.name} for changes — you'll get a notification if anything changed.`);
      setTimeout(async () => {
        try {
          const res = await monitorAPI.list();
          setCompanies(mapMonitors(res.data));
        } catch (err) { /* keep existing list */ }
        setCheckingId(null);
      }, 12000);
    } catch (err) {
      console.error("Check now failed:", err);
      toast.error(err?.response?.data?.detail || "Could not start the check. Please try again.");
      setCheckingId(null);
    }
  };

  // Toggle monitor pause/resume with optimistic update + revert-on-error.
  const handleTogglePause = async (co) => {
    if (!co || !co.monitor_id) {
      toast.error("Cannot pause this monitor — missing monitor id.");
      return;
    }
    const newStatus = co.status === "Active" ? "Paused" : "Active";
    const apiStatus = newStatus === "Active" ? "active" : "paused";
    setCompanies(prev => prev.map(c => c.monitor_id === co.monitor_id ? { ...c, status: newStatus } : c));
    try {
      await monitorAPI.update(co.monitor_id, { status: apiStatus });
      toast.success(newStatus === "Paused" ? "Monitoring paused." : "Monitoring resumed.");
    } catch (err) {
      console.error("Toggle monitor failed:", err);
      // Revert
      setCompanies(prev => prev.map(c => c.monitor_id === co.monitor_id ? { ...c, status: co.status } : c));
      toast.error("Could not update monitoring status.");
    }
  };

  const filtered = companies.filter(c => {
    if (filterStatus !== "All" && c.status !== filterStatus) return false;
    return true;
  });

  const materialCount = companies.filter(c => c.severity === "material").length;
  const minorCount = companies.filter(c => c.severity === "minor").length;

  const handleOpenResults = async (co) => {
    if (!onViewAnalysis || !workspaceId) return false;
    try {
      const res = await analysisAPI.list(workspaceId);
      const items = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      if (items.length > 0) {
        const matches = items.filter(a => {
          const aDomain = (a.domain || a.company_url || "").toLowerCase();
          const aName = (a.company_name || "").toLowerCase();
          const coDomain = (co.domain || "").toLowerCase();
          const coName = (co.name || "").toLowerCase();
          return (aDomain && coDomain && (aDomain.includes(coDomain) || coDomain.includes(aDomain))) ||
                 (aName && coName && (aName.includes(coName) || coName.includes(aName)));
        });
        const completed = matches.find(a => a.status === "completed");
        const best = completed || matches[0];
        if (best) {
          onViewAnalysis(best.analysis_id);
          return true;
        }
      }
      return false;
    } catch (err) { console.error("Failed to open results:", err); return false; }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
      `}</style>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 28px" }}>
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
          {[
            { label: "Total Monitored", value: companies.length, color: TEXT_PRIMARY },
            { label: "Active", value: companies.filter(c => c.status === "Active").length, color: "#22C55E" },
            { label: "Material Changes", value: materialCount, color: "#EF4444" },
            { label: "Minor Changes", value: minorCount, color: "#F59E0B" },
          ].map((s, i) => (
            <div key={i} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px" }}>
              <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 6, letterSpacing: "0.04em" }}>{s.label.toUpperCase()}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: s.color, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {["All", "Active", "Paused"].map(f => (
              <button key={f} onClick={() => setFilterStatus(f)} style={{
                fontSize: 12, padding: "6px 14px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${filterStatus === f ? ACCENT : BORDER}`,
                background: filterStatus === f ? `${ACCENT}18` : "transparent",
                color: filterStatus === f ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
              }}>{f}</button>
            ))}
          </div>
          <div style={{ marginLeft: "auto" }}>
            <input placeholder="Search companies…" style={{
              background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7,
              padding: "8px 14px", fontSize: 13, color: TEXT_PRIMARY, outline: "none",
              fontFamily: "'DM Sans', sans-serif", width: 220,
            }}
              onFocus={e => e.target.style.borderColor = ACCENT}
              onBlur={e => e.target.style.borderColor = BORDER}
            />
          </div>
        </div>

        {/* Company list */}
        <div>
          {companies.length === 0 ? (
            <div data-testid="monitored-empty-state" style={{ textAlign: "center", padding: "80px 16px" }}>
              <p style={{ fontSize: 15, color: TEXT_PRIMARY, marginBottom: 8 }}>No monitored companies yet.</p>
              <p style={{ fontSize: 13, color: TEXT_MUTED }}>Open any analysis and click <strong style={{color: TEXT_PRIMARY}}>Monitor</strong> to start tracking a company.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 16px" }}>
              <p style={{ fontSize: 14, color: TEXT_MUTED }}>No companies match this filter.</p>
            </div>
          ) : (filtered.map(co => (
            <CompanyRow
              key={co.id}
              co={co}
              onViewChange={setChangeTarget}
              onOpenResults={handleOpenResults}
              onTogglePause={handleTogglePause}
              onCheckNow={handleCheckNow}
              checking={checkingId === co.monitor_id}
            />
          )))}
        </div>
      </div>

      {/* Change detail panel */}
      {changeTarget && <ChangeDetailPanel company={changeTarget} onClose={() => setChangeTarget(null)} />}
    </div>
  );
}
